import { Input } from "../../../components/ui/input";
import { Upload as AntUpload, DatePicker, Image, Select } from "antd";
import { Button } from "../../../components/ui/button";
import { Eye, File, FileText, Save, Trash, UploadCloudIcon, X } from "lucide-react";
import { cn } from "../../../utils/tailwindUtils";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { EMPLOYEE_CANDIDATE_ISSUEDDOC, EMPLOYEE_INTERN_ISSUEDDOC, EMPLOYEE_PERSONAL_INFO, EMPLOYEEISSUEDDOC, LETTER_TYPE_API } from "../../../constants/api";
import ToastMsg from "../../../components/common/ToastMsg";
import useApiMethods from "../../../lib/useApiMethods";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { ConfirmPopupModal } from "../../../components/ui/dialogModal";
import { canAccessEmployeeProfile } from "../../../utils/employeeProfileAccess";
import { EmployeeProfileViewOnlyBanner } from "../../../components/employee/EmployeeProfileAccessNotice";

export const process_type_name = [
    { value: "on_bording", text: 'On-Boarding' },
    { value: "off_bording", text: 'Off-Boarding' },
]

export const letterStatusEnum = [
    { value: 'provided', text: 'Provided' },
    { value: 'not_provided', text: 'Not Provided' },
]

const IssuedDocumentContent = ({ employeeID, url = "" }) => {
    // state
    const [formData, setFormData] = useState({
        process_type: null,
        letter_type: null,
        processed_by: null,
        approved_by: null,
        letter_provided_date: null,
        letter_status: null,
        uploaded_documents: []
    });
    const [documentList, setDocumentList] = useState([]);
    const [deleteItem, setDeleteClicked] = useState({
        record: undefined,
        isDeleteClicked: false,
        letter_type_id: undefined
    });
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false); // Missing state
    const [selectedRow, setSelectedRow] = useState(null); // Missing state

    const [dropdownData, setDropdownData] = useState({
        empInfo: [],
        temp: []
    });
    const [dropdownsLoaded, setDropdownsLoaded] = useState(false);
    const [loading, setLoading] = useState(false);

    // variables
    const { fetch, create, remove } = useApiMethods();
    let location = useLocation();
    url = location.pathname === '/employee/viewDetails' ?
        EMPLOYEEISSUEDDOC : location.pathname === '/employee/onboarding/viewDetails' ?
            EMPLOYEE_CANDIDATE_ISSUEDDOC : location.pathname === '/intern/onboarding/viewDetails' ?
                EMPLOYEE_INTERN_ISSUEDDOC : ''; // updating the url based on the route

    const canEditDocuments = canAccessEmployeeProfile('documents', 'edit');

    const letterStatusEnum = [ // Added missing constant
        { value: 'provided', text: 'Provided' },
        { value: 'pending', text: 'Pending' },
        { value: 'not_provided', text: 'Not Provided' }
        // Add your actual statuses here
    ];

    // CSS classes (assuming these exist)
    const formSelect = "w-full";
    const formDatepicker = "w-full";

    // api calls
    const getEmployeeList = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_PERSONAL_INFO, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const getTemplateList = useCallback(async () => {
        try {
            const apiData = await fetch(LETTER_TYPE_API, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const fetchDropdown = async () => {
        setLoading(true);
        try {
            const [empInfo, temp] = await Promise.all([
                getEmployeeList(),
                getTemplateList()
            ]);

            let dropdown = {
                empInfo: empInfo || [],
                temp: temp || []
            };

            setDropdownData(dropdown);
            setDropdownsLoaded(true);
        } catch (error) {
            ToastMsg('error', error.message);
            setDropdownsLoaded(false);
        } finally {
            setLoading(false);
        }
    };

    const getEmployeeIssuedDocDetails = async (employeeID) => {
        if (!dropdownsLoaded) {
            console.log('Dropdowns not loaded yet, skipping employee data fetch');
            return;
        }

        setLoading(true);
        try {
            const apiData = await fetch(url, { employee_id: employeeID });
            if (apiData?.status === "success" && apiData.data) {
                setDocumentList(apiData.data || []);
            }
        } catch (error) {
            ToastMsg('error', error.message);
        } finally {
            setLoading(false);
        }
    };

    // Form handlers
    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleFileUpload = (file) => {
        setFormData(prev => ({
            ...prev,
            uploaded_documents: file ? [file] : []
        }));
    };

    const handleRemoveFile = () => {
        setFormData(prev => ({
            ...prev,
            uploaded_documents: []
        }));
    };

    // Validation function
    const validateForm = () => {
        const errors = [];

        if (!formData.process_type) {
            errors.push("Process Type is required");
        }
        if (!formData.letter_type) {
            errors.push("Letter Type is required");
        }
        if (!formData.processed_by) {
            errors.push("Processed By is required");
        }
        if (!formData.approved_by) {
            errors.push("Approved By is required");
        }
        if (!formData.letter_provided_date) {
            errors.push("Letter Provided Date is required");
        }
        if (!formData.letter_status) {
            errors.push("Letter Status is required");
        }
        if (formData.uploaded_documents.length === 0) {
            errors.push("Please upload at least one document");
        }

        return errors;
    };

    // clear function - Fixed incomplete function
    const handleClear = () => {
        setFormData({
            process_type: null,
            letter_type: null,
            processed_by: null,
            approved_by: null,
            letter_provided_date: null,
            letter_status: null,
            uploaded_documents: []
        });
    };

    // Save function
    const handleSave = async () => {
        try {
            setLoading(true);

            // Validate form
            const validationErrors = validateForm();
            if (validationErrors.length > 0) {
                ToastMsg('error', validationErrors[0]);
                return false;
            }

            // Prepare form data
            const submitData = new FormData();

            // Add all form fields
            submitData.append("process_type", formData.process_type);
            submitData.append("letter_type", formData.letter_type);
            submitData.append("processed_by", formData.processed_by);
            submitData.append("approved_by", formData.approved_by);
            submitData.append("letter_provided_date", formData.letter_provided_date.format("YYYY-MM-DD"));
            submitData.append("letter_status", formData.letter_status);
            submitData.append("employee_id", employeeID);

            // Add uploaded document if exists
            if (formData.uploaded_documents.length > 0 && formData.uploaded_documents[0]) {
                submitData.append("uploaded_documents", formData.uploaded_documents[0]);
            }

            const response = await create(url, submitData, true, setLoading);

            if (response.status !== 'fail') {
                ToastMsg('success', response.message || 'Document saved successfully');

                handleClear();

                if (employeeID) {
                    await getEmployeeIssuedDocDetails(employeeID);
                }

                return true;
            } else {
                ToastMsg('error', response.message || 'Failed to save document');
                return false;
            }

        } catch (error) {
            console.error('Save error:', error);
            ToastMsg('error', error.message || 'An error occurred while saving');
            return false;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const initializeAllData = async () => {
            await fetchDropdown();
        };

        initializeAllData();
    }, []);

    // useEffect for fetching employee data when dropdowns are loaded and employeeID is available
    useEffect(() => {
        if (employeeID && dropdownsLoaded) {
            getEmployeeIssuedDocDetails(employeeID);
        }
    }, [dropdownsLoaded, employeeID]);

    const handleDeleteClicked = (record) => {
        setDeleteClicked({
            record: record?.id,
            isDeleteClicked: true,
            letter_type_id: record?.letter_type
        });
        setSelectedRow(record);
        setIsDeleteConfirmOpen(true); // Open confirmation modal
    };

    const handleConfirmDelete = async () => {
        try {
            setLoading(true);
            const response = await remove(url + `?document_id=${deleteItem.record}&letter_type=${deleteItem.letter_type_id}&employee_id=${employeeID}`,);

            if (response.status !== 'fail') {
                ToastMsg('success', response.message || 'Document deleted successfully');

                // Refresh the document list
                if (employeeID) {
                    await getEmployeeIssuedDocDetails(employeeID);
                }

                setIsDeleteConfirmOpen(false);
                setSelectedRow(null);
                setDeleteClicked({
                    record: undefined,
                    isDeleteClicked: false,
                    letter_type_id: undefined
                });
            } else {
                ToastMsg('error', response.message || 'Failed to delete document');
            }
        } catch (error) {
            console.error('Delete error:', error);
            ToastMsg('error', error.message || 'An error occurred while deleting');
        } finally {
            setLoading(false);
        }
    };

    // loader
    if (loading && !dropdownsLoaded) {
        return (
            <div className="h-[calc(100vh-100px)] dark:bg-gray-900 flex items-center justify-center rounded-md">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header with Edit Button */}
            <div className="flex justify-between items-start mb-6">
                <div>
                    <h2 className="text-md font-bold text-gray-900 dark:text-white">
                        Issued Document Information
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Complete employee profile with issued document, professional information
                    </p>
                </div>
            </div>

            {!canEditDocuments && <EmployeeProfileViewOnlyBanner sectionLabel="issued documents" />}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Left Column - Form */}
                {canEditDocuments && (
                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Process Type */}
                        <FormField label="Process Type" required>
                            <Select
                                value={formData.process_type}
                                onChange={(value) => handleInputChange('process_type', value)}
                                placeholder="Select Process Type"
                                className={formSelect}
                                size="large"
                            >
                                {process_type_name.map(option => (
                                    <Select.Option key={option.value} value={option.value}>
                                        {option.text}
                                    </Select.Option>
                                ))}
                            </Select>
                        </FormField>

                        {/* Letter Type */}
                        <FormField label="Letter Type" required>
                            <Select
                                value={formData.letter_type}
                                onChange={(value) => handleInputChange('letter_type', value)}
                                placeholder="Select Letter Type"
                                className={formSelect}
                                size="large"
                            >
                                {dropdownData.temp.map(option => (
                                    <Select.Option key={option.id} value={option.id}>
                                        {option.letter_type_name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </FormField>

                        {/* Processed By */}
                        <FormField label="Processed By" required>
                            <Select
                                value={formData.processed_by}
                                onChange={(value) => handleInputChange('processed_by', value)}
                                placeholder="Select Processed By"
                                className={formSelect}
                                size="large"
                            >
                                {dropdownData.empInfo.map(option => (
                                    <Select.Option key={option.id} value={option.id}>
                                        {`${option.first_name || ""} ${option.last_name || ""} ${option.employee_id ? " - " + option.employee_id : ""}`}
                                    </Select.Option>
                                ))}
                            </Select>
                        </FormField>

                        {/* Approved By */}
                        <FormField label="Approved By" required>
                            <Select
                                value={formData.approved_by}
                                onChange={(value) => handleInputChange('approved_by', value)}
                                placeholder="Select Approved By"
                                className={formSelect}
                                size="large"
                            >
                                {dropdownData.empInfo.map(option => (
                                    <Select.Option key={option.id} value={option.id}>
                                        {`${option.first_name || ""} ${option.last_name || ""} ${option.employee_id ? " - " + option.employee_id : ""}`}
                                    </Select.Option>
                                ))}
                            </Select>
                        </FormField>

                        {/* Letter Provided Date */}
                        <FormField label="Letter Provided Date" required>
                            <DatePicker
                                allowClear
                                format={"DD-MM-YYYY"}
                                value={formData.letter_provided_date}
                                onChange={(date) => handleInputChange('letter_provided_date', date)}
                                size="large"
                                className={formDatepicker}
                                placeholder="Select Provided Date"
                            />
                        </FormField>

                        {/* Letter Status */}
                        <FormField label="Letter Status" required>
                            <Select
                                value={formData.letter_status}
                                onChange={(value) => handleInputChange('letter_status', value)}
                                placeholder="Select Status"
                                className={formSelect}
                                size="large"
                            >
                                {letterStatusEnum.map(option => (
                                    <Select.Option key={option.value} value={option.value}>
                                        {option.text}
                                    </Select.Option>
                                ))}
                            </Select>
                        </FormField>
                    </div>

                    {/* Upload Document - Full width */}
                    <div className="col-span-full">
                        <UploadField
                            label="Upload Document"
                            value={""}
                            icon={<File className="w-4 h-4" />}
                            attachment={formData.uploaded_documents}
                            onFileChange={handleFileUpload}
                            onRemoveFile={handleRemoveFile}
                            onInputChange={() => { }}
                        />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end items-center gap-3 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                        <Button
                            variant="outline"
                            onClick={handleClear}
                            disabled={loading}
                            className="flex items-center gap-2"
                        >
                            <X className="w-4 h-4" />
                            Clear
                        </Button>

                        <Button
                            disabled={loading}
                            variant="brand"
                            onClick={handleSave}
                            className="flex items-center gap-2 transition-colors duration-200"
                        >
                            {loading ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            ) : (
                                <Save className="w-4 h-4" />
                            )}
                            {loading ? "Processing..." : "Add Document Details"}
                        </Button>
                    </div>
                </div>
                )}

                {/* Right Column - Table */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-6">
                    <h3 className="text-md font-semibold text-gray-400 dark:text-white mb-3">
                        Issued History
                    </h3>

                    {documentList.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-gray-200 dark:border-gray-700">
                                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">
                                            Document Type
                                        </th>
                                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">
                                            Issued Date
                                        </th>
                                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">
                                            Status
                                        </th>
                                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {documentList.map((doc, index) => (
                                        <tr key={index} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                            <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">
                                                {dropdownData.temp.find(t => t.id === doc.letter_type)?.letter_type_name || 'Unknown Document'}
                                            </td>
                                            <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                                                {dayjs(doc.letter_provided_date).format('DD-MM-YYYY')}
                                            </td>
                                            <td className="py-3 px-4">
                                                <span className={cn(
                                                    "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                                                    doc.letter_status === 'provided'
                                                        ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                                                        : doc.letter_status === 'pending'
                                                            ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300"
                                                            : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                                                )}>
                                                    {letterStatusEnum.find(s => s.value === doc.letter_status)?.text || doc.letter_status}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 flex gap-3">
                                                {Array.isArray(doc.attachment) && doc.attachment[0] && (
                                                    <div className="flex gap-3">
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => window.open(doc.attachment[0], '_blank')}
                                                            className="flex items-center gap-1"
                                                        >
                                                            <Eye className="w-3 h-3" />
                                                        </Button>

                                                        {canEditDocuments && (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => { handleDeleteClicked(doc) }}
                                                            className="flex items-center gap-1 bg-red-400 hover:bg-red-300 text-white hover:text-white"
                                                        >
                                                            <Trash className="w-3 h-3" />
                                                        </Button>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                            <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p>No documents issued yet</p>
                            <p className="text-sm">Documents will appear here once added</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <ConfirmPopupModal
                open={isDeleteConfirmOpen}
                onClose={() => {
                    setIsDeleteConfirmOpen(false);
                    setSelectedRow(null);
                }}
                onConfirm={handleConfirmDelete}
                title="Delete the issued Document"
                description="Are you sure you want to delete this Issued Document? This action cannot be undone."
                type="warning"
                confirmlabel="Delete"
                destructive={true}
                loading={loading}
            />
        </div>
    );
};

// UploadField component (same as before but with proper prop handling)
const UploadField = ({ label, value, icon, attachment, onFileChange, onRemoveFile, onInputChange, flex = false }) => {
    // Safe file type checking
    const isFileObject = attachment &&
        Array.isArray(attachment) &&
        attachment.length > 0 &&
        typeof attachment[0] === 'object' &&
        attachment[0] !== null &&
        'name' in attachment[0] &&
        'size' in attachment[0] &&
        'type' in attachment[0];

    const isStringUrl = attachment &&
        Array.isArray(attachment) &&
        attachment.length > 0 &&
        typeof attachment[0] === 'string';

    const hasAttachment = isFileObject || isStringUrl;

    // Helper function to check if file is an image
    const isImageFile = (file) => {
        if (isFileObject) {
            return file.type?.startsWith("image/");
        }
        if (isStringUrl) {
            return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(file);
        }
        return false;
    };

    // Get file name for display
    const getFileName = () => {
        if (isFileObject) {
            return attachment[0].name;
        }
        if (isStringUrl) {
            try {
                const parsed = new URL(attachment[0], window.location.origin);
                const disp = parsed.searchParams.get("response-content-disposition");
                if (disp) {
                    const star = disp.match(/filename\*=UTF-8''([^;]+)/i);
                    if (star) return decodeURIComponent(star[1].trim());
                    const quoted = disp.match(/filename="([^"]+)"/i);
                    if (quoted) return quoted[1];
                }
                const last = parsed.pathname.split("/").pop();
                if (last) return decodeURIComponent(last);
            } catch (_) {
                const urlParts = attachment[0].split("/");
                return urlParts[urlParts.length - 1] || "Issued Document";
            }
        }
        return "Issued Document";
    };

    // Get file URL for preview
    const getFileUrl = () => {
        if (isFileObject) {
            return URL.createObjectURL(attachment[0]);
        }
        if (isStringUrl) {
            return attachment[0];
        }
        return '';
    };

    return (
        <div className={cn(
            "flex flex-col gap-3 rounded-lg border-gray-200 dark:border-gray-700 transition-colors duration-200",
            !flex ? "p-4 border bg-gray-50/50 dark:bg-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/70" : ''
        )}>
            <div className="flex items-center gap-3">
                {!flex && (
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-white dark:bg-gray-600 text-gray-600 dark:text-gray-300 shadow-sm">
                        {icon}
                    </div>
                )}
                <div className="flex-1 mb-1">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block">
                        {label}
                    </label>
                </div>
            </div>

            <div className={`${flex ? 'flex gap-2' : 'space-y-2'}`}>
                <div className="flex gap-2 items-center w-full">
                    {!hasAttachment ? (
                        <AntUpload
                            accept=".doc,.docx,.pdf,image/*"
                            maxCount={1}
                            beforeUpload={() => false}
                            onChange={({ fileList }) => {
                                if (fileList.length > 0 && fileList[0].originFileObj) {
                                    onFileChange(fileList[0].originFileObj);
                                }
                            }}
                            showUploadList={false}
                            className="w-full"
                        >
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="w-full border-dashed border-gray-300 dark:border-gray-600 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 dark:bg-blue-900 dark:text-white transition-colors"
                            >
                                <UploadCloudIcon className="w-4 h-4 mr-2" />
                                Upload Document
                            </Button>
                        </AntUpload>
                    ) : (
                        <div className="w-full">
                            {isImageFile(attachment[0]) ? (
                                // IMAGE PREVIEW
                                <div className="flex justify-between items-center gap-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-background">
                                    <div className="flex items-center gap-3 flex-1">
                                        <Image
                                            src={getFileUrl()}
                                            alt="preview"
                                            width={40}
                                            height={40}
                                            className="rounded-md shadow-sm object-cover"
                                        />
                                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                                            {getFileName()}
                                        </span>
                                    </div>
                                    <div className="flex gap-1">
                                        <a
                                            href={getFileUrl()}
                                            download={getFileName()}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="p-2 text-blue-600 border border-blue-200 rounded-md hover:bg-blue-50 dark:border-blue-800 dark:hover:bg-blue-900/20 transition-colors"
                                        >
                                            <Eye className="w-4 h-4" />
                                        </a>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={onRemoveFile}
                                            className="p-2 text-red-600 border-red-200 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20 transition-colors"
                                        >
                                            <Trash className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                // DOCUMENT PREVIEW
                                <div className="flex justify-between items-center gap-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-background">
                                    <div className="flex items-center gap-3 flex-1">
                                        <FileText className="w-8 h-8 text-blue-500" />
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                {getFileName()}
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                {isFileObject ?
                                                    (attachment[0].type === "application/pdf" ? "PDF Document" : "Document")
                                                    : "Uploaded Document"}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex gap-1">
                                        <a
                                            href={getFileUrl()}
                                            download={getFileName()}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="p-2 text-blue-600 border border-blue-200 rounded-md hover:bg-blue-50 dark:border-blue-800 dark:hover:bg-blue-900/20 transition-colors"
                                        >
                                            <Eye className="w-4 h-4" />
                                        </a>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={onRemoveFile}
                                            className="p-2 text-red-600 border-red-200 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20 transition-colors"
                                        >
                                            <Trash className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const FormField = ({ label, required = false, children, className }) => (
    <div className={cn("space-y-2", className)}>
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
            {label}
            {required && <span className="text-red-500 text-xs">*</span>}
        </label>
        {children}
    </div>
);

const formStyles = {
    input: cn(
        "w-full rounded-md transition-colors duration-200",
        "border-gray-300 dark:border-gray-600",
        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
    ),
    select: cn(
        "w-full rounded-md transition-colors duration-200",
        "border-gray-300 dark:border-gray-600",
        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
    ),
    textarea: cn(
        "w-full rounded-md transition-colors duration-200",
        "border-gray-300 dark:border-gray-600",
        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
        "disabled:text-muted-foreground disabled:opacity-60 resize-none"
    ),
    datepicker: cn(
        "w-full rounded-md transition-colors duration-200",
        "border-gray-300 dark:border-gray-600",
        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
    )
};

const { input: formInput, select: formSelect, textarea: formTextarea, datepicker: formDatepicker } = formStyles;

export default IssuedDocumentContent;